import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { TableSkeleton, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';
import type { Extension } from '@arcnet/contracts';

/**
 * What this seat can do, and where each answer came from.
 *
 * The screen exists to make one distinction visible that a plain list of
 * switches cannot: whether a feature is on because the PRODUCT includes it, or
 * because somebody switched it on for this person. Those look identical until
 * the product changes, and then the difference is the whole story -- seats on
 * the type follow it, seats with an override do not.
 *
 * So every row says which it is, and an overridden row offers a way back to the
 * type rather than only a way to flip the switch.
 */

interface ResolvedFeature {
  key: string;
  name: string;
  description: string;
  category: string;
  unit_cost?: string;
  from_type: boolean;
  overridden: boolean;
  enabled: boolean;
  reason: string | null;
}

interface FeatureResponse {
  data: ResolvedFeature[];
  extension_type_id: string | null;
  extension_type_name: string | null;
}

const CATEGORY_LABEL: Record<string, string> = {
  telephony: 'Telephony',
  contact_centre: 'Contact centre',
  endpoint: 'Devices & apps',
  collaboration: 'Collaboration',
  messaging: 'Messaging',
  analytics: 'Analytics',
  integration: 'Integration',
};

export function ExtensionFeaturesTab({ tenantId, ext }: {
  tenantId: string; ext: Extension;
}) {
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  const writable = can('extension:package:write');

  const key = ['tenants', tenantId, 'extensions', ext.id, 'features'] as const;
  const features = useQuery({
    queryKey: key,
    queryFn: () => api.get<FeatureResponse>(
      `/v1/tenants/${tenantId}/extensions/${ext.id}/features`),
  });

  const set = useMutation({
    mutationFn: ({ feature, enabled }: { feature: string; enabled: boolean | null }) =>
      api.put(`/v1/tenants/${tenantId}/extensions/${ext.id}/features/${feature}`, { enabled }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });

  if (features.error) return <ErrorBanner error={features.error} />;
  if (features.isLoading) return <div className="arc-card"><TableSkeleton rows={6} cols={3} /></div>;

  const rows = features.data?.data ?? [];
  const typeName = features.data?.extension_type_name;
  const overrides = rows.filter((f) => f.overridden).length;

  /** Grouped, because seventeen switches in one column is a list nobody reads. */
  const byCategory = rows.reduce<Record<string, ResolvedFeature[]>>((acc, f) => {
    (acc[f.category] ??= []).push(f);
    return acc;
  }, {});

  return (
    <>
      <div className="arc-banner arc-banner-info" style={{ marginBottom: 14 }}>
        <Icon name="info" size={14} />
        <span>
          {typeName ? (
            <>
              This seat is a <strong>{typeName}</strong>, which decides its
              features. Changing one here makes an exception for this extension
              only — the rest of the type is unaffected, and so is this seat's
              other features.
            </>
          ) : (
            <>
              This seat has <strong>no extension type</strong>, so nothing is
              included by default and every feature below is off unless switched
              on individually. Assigning a type in Details is usually
              better than switching features on one at a time.
            </>
          )}
        </span>
      </div>

      {overrides > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            {overrides === 1
              ? 'One feature differs from the type.'
              : `${overrides} features differ from the type.`}{' '}
            Exceptions do not follow the product when it changes, so they are
            worth revisiting when a plan is revised.
          </span>
        </div>
      )}

      {/*
        The groups FLOW, they do not stack.
        Measured on the rendered page: seventeen features across seven cards ran
        1034px tall, and 315px of that was card headings -- three of the cards
        held a single toggle each and cost as much vertical space as a card
        holding two. Stacking them put most of the feature set below the fold
        whatever the columns inside each card did.

        Multi-column keeps each group intact (`break-inside: avoid`) while
        letting a one-item group sit beside another instead of claiming a full
        width of its own. Grouping is what makes a feature findable, so it
        survives; only the empty space does not.
      */}
      <div style={{ columnWidth: 430, columnGap: 12 }}>
        {Object.entries(byCategory).map(([category, list]) => (
          <div key={category} className="arc-card"
            style={{ breakInside: 'avoid', marginBottom: 12 }}>
            <div className="arc-card-head">
              <h2 className="arc-h2">{CATEGORY_LABEL[category] ?? category}</h2>
            </div>
            {/*
              A GRID, not a stack.
              One feature per row left three quarters of the width empty and
              pushed seventeen toggles over several screens, so choosing a
              feature set meant scrolling past most of it. Columns fill to the
              width available and collapse to one on a narrow window.
            */}
            <div
              className="arc-card-body"
              style={{
                paddingTop: 8, paddingBottom: 10,
                display: 'grid',
                /* 300px because the widest item measures 306 at its natural
                   width and a column narrower than that wraps the description.
                   330 was picked without measuring and missed fitting a third
                   column by three pixels. */
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                columnGap: 22, rowGap: 2,
              }}
            >
              {list.map((f) => (
                <div
                  key={f.key}
                  className="arc-row"
                  style={{
                    gap: 12, alignItems: 'flex-start',
                    padding: '9px 0',
                  }}
                >
                  <Switch
                    id={`feat-${f.key}`}
                    checked={f.enabled}
                    disabled={!writable || set.isPending}
                    onChange={(next) => {
                      /**
                       * Flipping BACK to what the type says clears the override
                       * rather than pinning the same value.
                       *
                       * Otherwise switching a feature off and on again leaves a
                       * row that silently stops following the product, and the
                       * screen would look identical to one that does.
                       */
                      set.mutate({
                        feature: f.key,
                        enabled: next === f.from_type ? null : next,
                      });
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="arc-row" style={{ gap: 7 }}>
                      <label htmlFor={`feat-${f.key}`}
                        style={{ fontWeight: 550, cursor: writable ? 'pointer' : 'default' }}>
                        {f.name}
                      </label>
                      {f.overridden ? (
                        <Badge tone="warn">
                          {f.enabled ? 'added for this seat' : 'withheld'}
                        </Badge>
                      ) : f.from_type ? (
                        <Badge tone="muted">from {typeName ?? 'type'}</Badge>
                      ) : null}
                      {Number(f.unit_cost) > 0 && (
                        <span className="arc-subtle arc-num" style={{ fontSize: 11.5 }}>
                          our cost {Number(f.unit_cost).toFixed(2)}/month
                        </span>
                      )}
                    </div>
                    <div className="arc-subtle" style={{ marginTop: 2 }}>{f.description}</div>
                    {f.overridden && (
                      <button
                        type="button"
                        className="arc-btn arc-btn-ghost arc-btn-sm"
                        style={{ marginTop: 6 }}
                        disabled={!writable || set.isPending}
                        onClick={() => set.mutate({ feature: f.key, enabled: null })}
                      >
                        Reset to {typeName ? `the ${typeName} default` : 'the type default'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
